import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

const EstimateDownloadButton = lazy(() => import('../components/EstimateDownloadButton'));

// ── Constants ──────────────────────────────────────────────────────────────

const BUNDLE_GROUPS = [
  { label: 'Standard WorkSpaces', bundles: ['value', 'standard', 'performance', 'power', 'powerpro'] },
  { label: 'Graphics G4dn',       bundles: ['graphics.g4dn', 'graphicspro.g4dn'] },
  { label: 'Graphics G6',         bundles: ['graphics.g6.xlarge', 'graphics.g6.2xlarge', 'graphics.g6.4xlarge', 'graphics.g6.8xlarge', 'graphics.g6.16xlarge'] },
  { label: 'Graphics G6f',        bundles: ['graphics.g6f.large', 'graphics.g6f.xlarge', 'graphics.g6f.2xlarge', 'graphics.g6f.4xlarge', 'graphics.gr6f.4xlarge'] },
];

const BUNDLE_ORDER = BUNDLE_GROUPS.flatMap(g => g.bundles);

// Standard AWS storage configs per bundle, smallest-first (first = default minimum).
// G6 bundles are excluded — their storage is fixed per compute size via BUNDLE_INFO.storageKey.
// G6f bundles are excluded — not yet in pricing data.
const BUNDLE_STORAGE_OPTIONS = {
  'value':            ['80/10', '80/50', '80/100', '175/100'],
  'standard':         ['80/10', '80/50', '80/100', '175/100'],
  'performance':      ['80/10', '80/50', '80/100', '175/100'],
  'power':            ['80/10', '80/50', '80/100', '175/100'],
  'powerpro':         ['175/100'],
  'graphics.g4dn':    ['100/100'],
  'graphicspro.g4dn': ['100/100'],
};

// Optional pricingKey: S3 bundle group key when it differs from the entry's own key.
// Optional storageKey: specific storage variant to use (required when a bundle group
// holds multiple distinct products keyed by storage, e.g. all G6 sizes under 'graphics g6').
const BUNDLE_INFO = {
  'value':                { name: 'Value',                desc: '2 vCPU · 2 GB RAM · light tasks' },
  'standard':             { name: 'Standard',             desc: '2 vCPU · 4 GB RAM · general office use' },
  'performance':          { name: 'Performance',          desc: '2 vCPU · 8 GB RAM · productivity apps' },
  'power':                { name: 'Power',                desc: '4 vCPU · 16 GB RAM · power users & dev' },
  'powerpro':             { name: 'PowerPro',             desc: '8 vCPU · 32 GB RAM · data/BI workloads' },
  'graphics.g4dn':        { name: 'Graphics G4dn',        desc: '4 vCPU · 16 GB RAM · NVIDIA T4 GPU' },
  'graphicspro.g4dn':     { name: 'GraphicsPro G4dn',     desc: '16 vCPU · 64 GB RAM · NVIDIA T4 GPU' },
  'graphics.g6.xlarge':   { name: 'Graphics G6 xlarge',   desc: '4 vCPU · 16 GB RAM · NVIDIA L4 GPU',   pricingKey: 'graphics g6', storageKey: '100/100' },
  'graphics.g6.2xlarge':  { name: 'Graphics G6 2xlarge',  desc: '8 vCPU · 32 GB RAM · NVIDIA L4 GPU',   pricingKey: 'graphics g6', storageKey: '150/100' },
  'graphics.g6.4xlarge':  { name: 'Graphics G6 4xlarge',  desc: '16 vCPU · 64 GB RAM · NVIDIA L4 GPU',  pricingKey: 'graphics g6', storageKey: '200/150' },
  'graphics.g6.8xlarge':  { name: 'Graphics G6 8xlarge',  desc: '32 vCPU · 128 GB RAM · NVIDIA L4 GPU', pricingKey: 'graphics g6', storageKey: '250/200' },
  'graphics.g6.16xlarge': { name: 'Graphics G6 16xlarge', desc: '64 vCPU · 256 GB RAM · NVIDIA L4 GPU', pricingKey: 'graphics g6', storageKey: '300/200' },
  'graphics.g6f.large':    { name: 'Graphics G6f large',    desc: '2 vCPU · 8 GB RAM · 1/8 NVIDIA L4 GPU' },
  'graphics.g6f.xlarge':   { name: 'Graphics G6f xlarge',   desc: '4 vCPU · 16 GB RAM · 1/4 NVIDIA L4 GPU' },
  'graphics.g6f.2xlarge':  { name: 'Graphics G6f 2xlarge',  desc: '8 vCPU · 32 GB RAM · 1/2 NVIDIA L4 GPU' },
  'graphics.g6f.4xlarge':  { name: 'Graphics G6f 4xlarge',  desc: '16 vCPU · 64 GB RAM · NVIDIA L4 GPU' },
  'graphics.gr6f.4xlarge': { name: 'Graphics Gr6f 4xlarge', desc: '16 vCPU · 128 GB RAM · NVIDIA L4 GPU' },
};

const USE_CASES = [
  { category: 'General', items: [
    { label: 'Web browsing',  bundle: 'value' },
    { label: 'Email',         bundle: 'standard' },
    { label: 'Text editing',  bundle: 'value' },
    { label: 'Data entry',    bundle: 'value' },
  ]},
  { category: 'Productivity', items: [
    { label: 'Word processing',  bundle: 'performance' },
    { label: 'Spreadsheets',     bundle: 'performance' },
    { label: 'Audio processing', bundle: 'performance' },
    { label: 'Courseware',       bundle: 'performance' },
  ]},
  { category: 'Audio / Video', items: [
    { label: 'Audio/video conferencing', bundle: 'power' },
    { label: 'Screen sharing',           bundle: 'powerpro' },
    { label: 'Video transcoding',        bundle: 'graphics.g4dn' },
    { label: 'Game streaming',           bundle: 'graphics.g4dn' },
  ]},
  { category: 'Data & Analytics', items: [
    { label: 'Entry to mid-level data processing', bundle: 'power' },
    { label: 'Data warehousing',                   bundle: 'powerpro' },
    { label: 'Business intelligence apps',         bundle: 'powerpro' },
  ]},
  { category: 'Development', items: [
    { label: 'Software development (IDE)', bundle: 'power' },
    { label: 'Machine learning (CPU)',     bundle: 'powerpro' },
  ]},
  { category: 'Graphics', items: [
    { label: 'Graphic design',         bundle: 'graphics.g4dn' },
    { label: 'CAD/CAM',                bundle: 'graphics.g4dn' },
    { label: '3D rendering',           bundle: 'graphics.g4dn' },
    { label: 'Photo-realistic design', bundle: 'graphics.g4dn' },
    { label: 'Image processing',       bundle: 'graphics.g4dn' },
  ]},
];

const CALC_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-2',
  'eu-west-1', 'eu-central-1',
  'ap-northeast-1', 'ap-southeast-1', 'ap-southeast-2',
  'ca-central-1',
];

const USAGE_PRESETS = [
  { label: 'Full time', sublabel: '~160 hrs/mo', hours: 160 },
  { label: 'Part-time',        sublabel: '~75 hrs/mo',  hours: 75  },
  { label: 'Occasional',       sublabel: '~20 hrs/mo',  hours: 20  },
];

const defaultGroup = () => ({
  id: Date.now() + Math.random(),
  label: '',
  mode: 'guided',
  os: 'windows',
  selectedUseCases: [],
  manualBundle: '',
  storageKey: null,
  count: 10,
  hours: 160,
});

// ── Helpers ────────────────────────────────────────────────────────────────

function getRecommendedBundle(useCases) {
  let maxIdx = -1;
  useCases.forEach(uc => {
    const idx = BUNDLE_ORDER.indexOf(uc.bundle);
    if (idx > maxIdx) maxIdx = idx;
  });
  return maxIdx >= 0 ? BUNDLE_ORDER[maxIdx] : null;
}

function formatStorageKey(key) {
  const [root, user] = key.split('/');
  return `${root} GB root / ${user} GB user`;
}

function resolveStorageKey(bundleKey, storageKey) {
  const options = BUNDLE_STORAGE_OPTIONS[bundleKey];
  if (!options) return null;
  if (storageKey && options.includes(storageKey)) return storageKey;
  return options[0];
}

function getBundleRates(bundleKey, pricingBundles, storageKey) {
  const info = BUNDLE_INFO[bundleKey];
  const pricingKey = info?.pricingKey ?? bundleKey;
  const bundleData = pricingBundles?.[pricingKey];
  if (!bundleData) return null;
  const key = storageKey || info?.storageKey;
  if (key) return bundleData[key] ?? null;
  return Object.values(bundleData).find(v => v?.always_on) || null;
}

function getClickRate(n) {
  if (n >= 10000) return 1.00;
  if (n >= 5000)  return 1.50;
  if (n >= 500)   return 2.50;
  return 3.00;
}

const fmt = (n) =>
  `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (n) => `$${Math.round(n ?? 0).toLocaleString('en-US')}`;

// ── Styles ─────────────────────────────────────────────────────────────────

const S = {
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    background: 'var(--color-primary)', color: 'white',
    padding: '0.25rem 0.625rem', borderRadius: '1rem', fontSize: '0.8125rem',
    fontWeight: '500',
  },
  chipRemove: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
    padding: '0 0 0 0.125rem', cursor: 'pointer', fontSize: '1rem',
    lineHeight: 1, display: 'flex', alignItems: 'center',
  },
  dropdownPanel: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
    background: 'white', border: '1px solid var(--color-border)',
    borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    minWidth: '280px', maxHeight: '340px', overflowY: 'auto',
  },
  categoryHeader: {
    padding: '0.4rem 1rem', fontWeight: '600', fontSize: '0.7rem',
    color: 'var(--color-text-secondary)', background: 'var(--color-bg)',
    textTransform: 'uppercase', letterSpacing: '0.06em', userSelect: 'none',
  },
  dropdownItem: {
    padding: '0.45rem 1rem 0.45rem 1.25rem', cursor: 'pointer',
    fontSize: '0.9rem', color: 'var(--color-text)', transition: 'background 0.1s',
  },
  modeBtn: (active) => ({
    padding: '0.4rem 0.875rem', borderRadius: '4px', fontSize: '0.8125rem',
    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
    background: active ? 'var(--color-primary)' : 'transparent',
    color: active ? 'white' : 'var(--color-text-secondary)',
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
    fontWeight: active ? '500' : 'normal',
  }),
  presetBtn: (active) => ({
    padding: '0.35rem 0.75rem', borderRadius: '4px', fontSize: '0.8rem',
    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
    background: active ? 'var(--color-primary)' : 'transparent',
    color: active ? 'white' : 'var(--color-text-secondary)',
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
    fontWeight: active ? '500' : 'normal',
  }),
  groupBox: {
    border: '1px solid var(--color-border)', borderRadius: '6px',
    padding: '1rem', marginBottom: '0.875rem',
  },
  highlightRow: { borderLeft: '3px solid var(--color-green)' },
  totalsRow: { fontWeight: '600', background: 'var(--color-bg)' },
  osBtn: (active) => ({
    padding: '0.25rem 0.625rem', borderRadius: '4px', fontSize: '0.75rem',
    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
    background: active ? 'var(--color-primary)' : 'transparent',
    color: active ? 'white' : 'var(--color-text-secondary)',
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
  }),
};

// ── Component ──────────────────────────────────────────────────────────────

export default function Calculator() {
  const [searchParams] = useSearchParams();
  const [step, setStep]                   = useState(1);
  const [groups, setGroups]               = useState([defaultGroup()]);
  const [openPickerId, setOpenPickerId]   = useState(null);
  const [region, setRegion]               = useState('us-east-1');
  const [pricingWindows, setPricingWindows] = useState(null);
  const [pricingLinux, setPricingLinux]     = useState(null);
  const [pricingSource, setPricingSource]   = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);

  // Edit-existing-estimate state
  const [estimateId, setEstimateId]         = useState(null);
  const [editingCreatedAt, setEditingCreatedAt] = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);

  // Save-estimate flow
  const [saveState, setSaveState]           = useState('idle'); // idle | form | saving | saved | error
  const [prospectName, setProspectName]     = useState('');
  const [prospectEmail, setProspectEmail]   = useState('');
  const [savedAt, setSavedAt]               = useState(null);

  const pickerRefs = useRef({});

  // Close picker on outside click
  useEffect(() => {
    if (!openPickerId) return;
    const handler = (e) => {
      const ref = pickerRefs.current[openPickerId];
      if (ref && !ref.contains(e.target)) setOpenPickerId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPickerId]);

  // Load saved estimate when ?estimateId is in the URL
  useEffect(() => {
    const id = searchParams.get('estimateId');
    if (!id) return;
    setLoadingEstimate(true);
    api.get(`/estimates/${id}`)
      .then(({ data }) => {
        setEstimateId(data.estimateId);
        setEditingCreatedAt(data.createdAt);
        setRegion(data.region || 'us-east-1');
        setProspectName(data.prospectName || '');
        setProspectEmail(data.contactEmail || '');
        const reconstructed = (data.groups || []).map(g => ({
          id: Date.now() + Math.random(),
          label:            g.label || '',
          mode:             'manual',
          os:               g.os || 'windows',
          selectedUseCases: [],
          manualBundle:     g.bundleKey || '',
          storageKey:       g.storageKey || null,
          count:            g.count || 10,
          hours:            g.hours || 160,
        }));
        setGroups(reconstructed.length > 0 ? reconstructed : [defaultGroup()]);
      })
      .catch(() => alert('Failed to load estimate.'))
      .finally(() => setLoadingEstimate(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch pricing for both OS types when entering step 2 or changing region
  useEffect(() => {
    if (step !== 2) return;
    setLoadingPricing(true);
    Promise.all([
      api.get('/pricing', { params: { region, os: 'windows' } }),
      api.get('/pricing', { params: { region, os: 'linux' } }),
    ])
      .then(([winRes, linRes]) => {
        setPricingWindows(winRes.data.bundles);
        setPricingLinux(linRes.data.bundles);
        setPricingSource(winRes.data.source === 'live' ? 'live' : 'unavailable');
      })
      .catch(() => { setPricingWindows(null); setPricingLinux(null); setPricingSource('unavailable'); })
      .finally(() => setLoadingPricing(false));
  }, [region, step]);

  // ── Group helpers ───────────────────────────────────────────────────────
  const updateGroup = (id, changes) =>
    setGroups(prev => prev.map(g => g.id === id ? { ...g, ...changes } : g));

  const addGroup = () => setGroups(prev => [...prev, defaultGroup()]);

  const removeGroup = (id) => setGroups(prev => prev.filter(g => g.id !== id));

  const addUseCase = (groupId, item) => {
    updateGroup(groupId, {
      selectedUseCases: [...(groups.find(g => g.id === groupId)?.selectedUseCases || []), item],
    });
    setOpenPickerId(null);
  };

  const removeUseCase = (groupId, label, e) => {
    e.stopPropagation();
    const g = groups.find(x => x.id === groupId);
    updateGroup(groupId, { selectedUseCases: (g?.selectedUseCases || []).filter(uc => uc.label !== label) });
  };

  // ── Save estimate ───────────────────────────────────────────────────────
  const buildEstimatePayload = () => ({
    prospectName: prospectName.trim(),
    contactEmail: prospectEmail.trim(),
    region,
    os: osDerived,
    groups: groupResults.map(g => ({
      label:        g.label,
      bundleKey:    g.bundleKey,
      bundleName:   BUNDLE_INFO[g.bundleKey]?.name,
      storageKey:   g.resolvedStorageKey,
      cheaperMode:  g.cheaperMode,
      breakEven:    g.breakEven,
      count:        g.count,
      hours:        g.hours,
      monthlyTotal: g.monthlyTotal,
      perUserMo:    g.monthlyTotal != null ? g.monthlyTotal / g.count : null,
      rates:        g.rates,
      os:           g.os,
    })),
    totalMonthly,
    totalWsCount,
    wsSavings,
    itSavings,
    clickMonthly,
    roi,
  });

  const handleSave = async () => {
    if (!prospectName.trim()) return;
    setSaveState('saving');
    try {
      const payload = buildEstimatePayload();
      if (estimateId) {
        await api.put(`/estimates/${estimateId}`, { ...payload, createdAt: editingCreatedAt });
      } else {
        await api.post('/estimates', payload);
      }
      setSavedAt(new Date().toISOString());
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  // ── Derived values (step 2) ─────────────────────────────────────────────
  const groupResults = groups.map(g => {
    const bundleKey          = g.mode === 'guided' ? getRecommendedBundle(g.selectedUseCases) : g.manualBundle;
    const pricingBundles     = g.os === 'linux' ? pricingLinux : pricingWindows;
    const resolvedStorageKey = bundleKey ? resolveStorageKey(bundleKey, g.storageKey) : null;
    const rates              = bundleKey ? getBundleRates(bundleKey, pricingBundles, resolvedStorageKey) : null;
    const hasAutoStop  = rates?.auto_stop_base != null && rates?.auto_stop_hr != null;
    const breakEven    = (rates?.always_on != null && hasAutoStop)
      ? Math.round((rates.always_on - rates.auto_stop_base) / rates.auto_stop_hr) : null;
    const aoMonthly    = rates?.always_on != null ? g.count * rates.always_on : null;
    const asMonthly    = hasAutoStop
      ? g.count * (rates.auto_stop_base + g.hours * rates.auto_stop_hr) : null;
    const cheaperMode  = aoMonthly != null
      ? (asMonthly != null && asMonthly < aoMonthly ? 'auto_stop' : 'always_on') : null;
    const monthlyTotal = cheaperMode === 'always_on' ? aoMonthly : asMonthly;
    return { ...g, bundleKey, resolvedStorageKey, rates, breakEven, aoMonthly, asMonthly, cheaperMode, monthlyTotal };
  });

  const totalMonthly  = groupResults.reduce((s, g) => s + (g.monthlyTotal ?? 0), 0);
  const totalWsCount  = groups.reduce((s, g) => s + g.count, 0);
  const allHaveRates  = pricingSource === 'live' && groupResults.every(g => g.bundleKey && g.rates);
  const anyMissing    = pricingSource === 'live' && groupResults.some(g => g.bundleKey && !g.rates);
  const uniqueOs      = [...new Set(groups.map(g => g.os))];
  const osDerived     = uniqueOs.length === 1 ? uniqueOs[0] : 'mixed';

  const wsSavings    = totalMonthly * 0.20;
  const itSavings    = totalWsCount * (10 / 60) * 60;  // 10 min/workspace/mo @ $60/hr
  const clickMonthly = totalWsCount * getClickRate(totalWsCount);
  const totalBenefit = wsSavings + itSavings;
  const roi          = clickMonthly > 0 ? (totalBenefit / clickMonthly) * 100 : null;

  const canContinue = groups.length > 0 && groups.every(g => {
    const hasBundle = g.mode === 'guided' ? g.selectedUseCases.length > 0 : g.manualBundle !== '';
    return hasBundle && g.count > 0;
  });

  // ── Render ──────────────────────────────────────────────────────────────
  if (loadingEstimate) {
    return (
      <div className="container">
        <div className="spinner" style={{ marginTop: '3rem' }} />
      </div>
    );
  }

  return (
    <div className="container">
      <h1 style={{ marginBottom: '0.25rem' }}>WorkSpaces Pricing Calculator</h1>
      {estimateId ? (
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>
          Editing estimate for <strong>{prospectName || 'unknown prospect'}</strong>. Make your changes and save to update.
        </p>
      ) : (
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>
          Estimate what a WorkSpaces environment would cost without needing to know AWS pricing details.
          Define one or more user groups to model mixed environments.
        </p>
      )}

      {/* ── Step 1: Configure groups ────────────────────────────────────── */}
      {step === 1 && (
        <div className="card">
          <h2>Configure user groups</h2>

          {groups.map((group, idx) => {
            const selectedLabels    = new Set(group.selectedUseCases.map(uc => uc.label));
            const recommendedBundle = getRecommendedBundle(group.selectedUseCases);
            const isPickerOpen      = openPickerId === group.id;

            return (
              <div key={group.id} style={S.groupBox}>
                {/* Group header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
                  <input
                    type="text"
                    placeholder={`Group ${idx + 1}`}
                    value={group.label}
                    onChange={e => updateGroup(group.id, { label: e.target.value })}
                    style={{ width: '180px', padding: '0.3rem 0.6rem', fontSize: '0.875rem', fontWeight: '500' }}
                  />
                  <span style={{ flex: 1 }} />
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button style={S.osBtn(group.os !== 'linux')} onClick={() => updateGroup(group.id, { os: 'windows' })}>
                      Windows
                    </button>
                    <button style={S.osBtn(group.os === 'linux')} onClick={() => updateGroup(group.id, { os: 'linux' })}>
                      Linux
                    </button>
                  </div>
                  {groups.length > 1 && (
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => removeGroup(group.id)}
                      style={{ color: 'var(--color-red)', fontSize: '0.8125rem' }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Mode toggle */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.875rem' }}>
                  <button style={S.modeBtn(group.mode === 'guided')} onClick={() => updateGroup(group.id, { mode: 'guided' })}>
                    {group.mode === 'guided' ? '● ' : '○ '}Help me choose
                  </button>
                  <button style={S.modeBtn(group.mode === 'manual')} onClick={() => updateGroup(group.id, { mode: 'manual' })}>
                    {group.mode === 'manual' ? '● ' : '○ '}I know the bundle
                  </button>
                </div>

                {/* Guided: chip picker */}
                {group.mode === 'guided' && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8125rem', display: 'block', marginBottom: '0.375rem' }}>
                      What will these users use WorkSpaces for?
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center', minHeight: '2rem' }}>
                      {group.selectedUseCases.map(uc => (
                        <span key={uc.label} style={S.chip}>
                          {uc.label}
                          <button style={S.chipRemove} onClick={(e) => removeUseCase(group.id, uc.label, e)}>×</button>
                        </span>
                      ))}
                      <div
                        ref={el => { pickerRefs.current[group.id] = el; }}
                        style={{ position: 'relative' }}
                      >
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => setOpenPickerId(isPickerOpen ? null : group.id)}
                        >
                          + Add use case
                        </button>
                        {isPickerOpen && (
                          <div style={S.dropdownPanel}>
                            {USE_CASES.map(grp => {
                              const available = grp.items.filter(it => !selectedLabels.has(it.label));
                              if (!available.length) return null;
                              return (
                                <div key={grp.category}>
                                  <div style={S.categoryHeader}>{grp.category}</div>
                                  {available.map(item => (
                                    <div
                                      key={item.label}
                                      style={S.dropdownItem}
                                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                                      onClick={() => addUseCase(group.id, item)}
                                    >
                                      {item.label}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    {recommendedBundle && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--color-primary)', fontWeight: '500' }}>
                        Recommended: <strong>{BUNDLE_INFO[recommendedBundle]?.name}</strong> — {BUNDLE_INFO[recommendedBundle]?.desc}
                      </div>
                    )}
                  </div>
                )}

                {/* Manual: bundle dropdown */}
                {group.mode === 'manual' && (
                  <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.8125rem' }}>Bundle type</label>
                    <select
                      value={group.manualBundle}
                      onChange={e => updateGroup(group.id, { manualBundle: e.target.value })}
                    >
                      <option value="">Choose a bundle...</option>
                      {BUNDLE_GROUPS.map(grp => (
                        <optgroup key={grp.label} label={grp.label}>
                          {grp.bundles.map(b => (
                            <option key={b} value={b}>{BUNDLE_INFO[b].name} — {BUNDLE_INFO[b].desc}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* Storage size selector */}
                {(() => {
                  const bk = group.mode === 'guided' ? getRecommendedBundle(group.selectedUseCases) : group.manualBundle;
                  const options = bk ? BUNDLE_STORAGE_OPTIONS[bk] : null;
                  if (!options || options.length <= 1) return null;
                  const effective = resolveStorageKey(bk, group.storageKey);
                  return (
                    <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                      <label style={{ fontSize: '0.8125rem' }}>Storage size</label>
                      <select
                        value={effective}
                        onChange={e => updateGroup(group.id, { storageKey: e.target.value })}
                        style={{ width: 'auto' }}
                      >
                        {options.map(key => (
                          <option key={key} value={key}>{formatStorageKey(key)}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Count + Usage row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', margin: 0 }}>Users</label>
                    <input
                      type="number"
                      min="1"
                      max="99999"
                      value={group.count}
                      onChange={e => updateGroup(group.id, { count: Math.max(1, parseInt(e.target.value) || 1) })}
                      style={{ width: '80px', padding: '0.35rem 0.5rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                    {USAGE_PRESETS.map(p => (
                      <button
                        key={p.hours}
                        style={S.presetBtn(group.hours === p.hours)}
                        onClick={() => updateGroup(group.id, { hours: p.hours })}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: '160px' }}>
                    <input
                      type="range"
                      min="0"
                      max="250"
                      value={group.hours}
                      onChange={e => updateGroup(group.id, { hours: parseInt(e.target.value) })}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                      {group.hours} hrs/mo
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          <button className="btn-secondary" onClick={addGroup} style={{ marginBottom: '1.25rem' }}>
            + Add another group
          </button>

          <button onClick={() => setStep(2)} disabled={!canContinue}>
            Continue →
          </button>
        </div>
      )}

      {/* ── Step 2: Review & Estimate ───────────────────────────────────── */}
      {step === 2 && (
        <>
          <button className="btn-ghost" onClick={() => setStep(1)} style={{ marginBottom: '1rem', fontSize: '0.9375rem' }}>
            ← Back to configure
          </button>

          {/* Configuration summary */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ margin: 0 }}>Your Configuration</h2>
              <button className="btn-secondary btn-sm" onClick={() => setStep(1)}>Edit</button>
            </div>
            <div style={{ marginTop: '0.875rem' }}>
              {groups.map((g, idx) => {
                const bundleKey = g.mode === 'guided' ? getRecommendedBundle(g.selectedUseCases) : g.manualBundle;
                const bundleName = bundleKey ? BUNDLE_INFO[bundleKey]?.name : '—';
                const displayLabel = g.label || `Group ${idx + 1}`;
                const usageLabel = USAGE_PRESETS.find(p => p.hours === g.hours)?.label || `${g.hours} hrs/mo`;
                const storageOptions = bundleKey ? BUNDLE_STORAGE_OPTIONS[bundleKey] : null;
                const sk = bundleKey ? resolveStorageKey(bundleKey, g.storageKey) : null;
                const storageSummary = storageOptions && storageOptions.length > 1 && sk ? ` · ${sk} GB` : '';
                return (
                  <div key={g.id} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.9rem', padding: '0.25rem 0', borderBottom: '1px solid var(--color-border-light)' }}>
                    <span style={{ fontWeight: '600', minWidth: '90px' }}>{displayLabel}</span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      {bundleName}{storageSummary} · {g.count} users · {usageLabel} · {g.os === 'linux' ? 'Linux' : 'Windows'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.625rem', fontSize: '0.875rem' }}>
                AWS Region
                <select value={region} onChange={e => setRegion(e.target.value)} style={{ width: 'auto', padding: '0.3rem 0.6rem', fontSize: '0.875rem' }}>
                  {CALC_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Cost estimate */}
          <div className="card">
            <h2>Cost Estimate</h2>
            {loadingPricing && <div className="spinner" />}

            {!loadingPricing && pricingSource === 'unavailable' && (
              <div className="alert info">
                <strong>Pricing data not yet loaded.</strong> Run the pricing refresh Lambda in the AWS Console:
                Lambda → <code>*-pricing-refresh-*</code> → Test tab → <code>{"{"}"refreshPricing": true{"}"}</code>
                — then refresh this page.
              </div>
            )}

            {!loadingPricing && anyMissing && (
              <div className="alert info" style={{ marginBottom: '0.75rem' }}>
                Some bundles don't have pricing data for <strong>{region}</strong>. Try a different region or contact your AWS account team.
              </div>
            )}

            {!loadingPricing && pricingSource === 'live' && (
              <table>
                <thead>
                  <tr>
                    <th>Group</th>
                    <th>Bundle</th>
                    <th>Running mode</th>
                    <th>Users</th>
                    <th>Per user/mo</th>
                    <th>Per user/yr</th>
                    <th>Total/mo</th>
                    <th>Total/yr</th>
                  </tr>
                </thead>
                <tbody>
                  {groupResults.map((g, idx) => {
                    const displayLabel = g.label || `Group ${idx + 1}`;
                    const bundleName   = g.bundleKey ? BUNDLE_INFO[g.bundleKey]?.name : '—';
                    if (!g.rates) {
                      return (
                        <tr key={g.id}>
                          <td>{displayLabel}</td>
                          <td>{bundleName}</td>
                          <td colSpan={6} style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                            Pricing not available in {region}
                          </td>
                        </tr>
                      );
                    }
                    const modeLabel  = g.cheaperMode === 'always_on' ? 'Always-On' : `AutoStop (${g.hours}h)`;
                    const perUserMo  = g.monthlyTotal != null ? g.monthlyTotal / g.count : null;
                    const perUserYr  = perUserMo != null ? perUserMo * 12 : null;
                    return (
                      <tr key={g.id} style={g.cheaperMode ? S.highlightRow : {}}>
                        <td>{displayLabel}</td>
                        <td>
                          {bundleName}
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            {g.os === 'linux' ? 'Linux' : 'Windows'}
                            {g.resolvedStorageKey && BUNDLE_STORAGE_OPTIONS[g.bundleKey]?.length > 1
                              ? ` · ${g.resolvedStorageKey} GB` : ''}
                          </span>
                        </td>
                        <td>
                          <span style={{ color: 'var(--color-green)', fontWeight: '500' }}>✓</span> {modeLabel}
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            {g.cheaperMode === 'auto_stop' && g.rates?.auto_stop_hr && g.rates?.auto_stop_base != null
                              ? `$${g.rates.auto_stop_base.toFixed(2)}/mo + $${g.rates.auto_stop_hr.toFixed(2)}/hr`
                              : `$${(g.rates?.always_on ?? 0).toFixed(2)}/mo`}
                          </span>
                        </td>
                        <td>{g.count}</td>
                        <td>{fmt(perUserMo)}</td>
                        <td>{fmt(perUserYr)}</td>
                        <td style={{ fontWeight: '500' }}>{fmtK(g.monthlyTotal)}</td>
                        <td>{fmtK((g.monthlyTotal ?? 0) * 12)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {groupResults.length > 1 && (
                  <tfoot>
                    <tr style={S.totalsRow}>
                      <td colSpan={3}><strong>Total</strong></td>
                      <td><strong>{totalWsCount}</strong></td>
                      <td>—</td>
                      <td>—</td>
                      <td><strong>{fmtK(totalMonthly)}</strong></td>
                      <td><strong>{fmtK(totalMonthly * 12)}</strong></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>

          {/* Click Pre-Flight ROI */}
          {allHaveRates && (
            <div className="card">
              <h2>Click Pre-Flight ROI</h2>
              <div className="alert info" style={{ marginBottom: '1.25rem' }}>
                Estimates use industry benchmarks: ~20% cost reduction typical, 10 min/workspace/month admin time saved.
                Run a real Pre-Flight analysis for your environment's actual numbers.
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Monthly</th>
                    <th>Annual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total WorkSpaces cost (recommended modes)</td>
                    <td>{fmtK(totalMonthly)}</td>
                    <td>{fmtK(totalMonthly * 12)}</td>
                  </tr>
                  <tr>
                    <td>Estimated cost savings (~20% typical)</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(wsSavings)}</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(wsSavings * 12)}</td>
                  </tr>
                  <tr>
                    <td>IT admin time savings (10 min/workspace @ $60/hr)</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(itSavings)}</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(itSavings * 12)}</td>
                  </tr>
                  <tr style={{ fontWeight: '600' }}>
                    <td>Total monthly benefit</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(totalBenefit)}</td>
                    <td style={{ color: 'var(--color-green)' }}>{fmtK(totalBenefit * 12)}</td>
                  </tr>
                  <tr>
                    <td>Click Pre-Flight cost (${getClickRate(totalWsCount).toFixed(2)}/workspace/mo)</td>
                    <td style={{ color: 'var(--color-red)' }}>{fmtK(clickMonthly)}</td>
                    <td style={{ color: 'var(--color-red)' }}>{fmtK(clickMonthly * 12)}</td>
                  </tr>
                  <tr style={{ fontWeight: '700', fontSize: '1.05rem' }}>
                    <td>ROI</td>
                    <td colSpan={2} style={{ color: roi >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                      {roi != null ? `${roi >= 0 ? '+' : ''}${Math.round(roi)}%` : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: '1.5rem' }}>
                {saveState === 'idle' && (
                  <button className="btn-success" onClick={() => setSaveState('form')}>
                    {estimateId ? 'Update Estimate' : 'Save & Download PDF'}
                  </button>
                )}

                {saveState === 'form' && (
                  <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '1rem', maxWidth: '480px' }}>
                    <p style={{ marginBottom: '0.75rem', fontWeight: '500', fontSize: '0.9375rem' }}>
                      {estimateId ? 'Update estimate' : 'Save this estimate'}
                    </p>
                    <div className="form-group">
                      <label>Prospect / company name *</label>
                      <input
                        type="text"
                        value={prospectName}
                        onChange={e => setProspectName(e.target.value)}
                        placeholder="Acme Corp"
                        autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact email (optional)</label>
                      <input
                        type="email"
                        value={prospectEmail}
                        onChange={e => setProspectEmail(e.target.value)}
                        placeholder="contact@acmecorp.com"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <button className="btn-secondary" onClick={() => setSaveState('idle')}>Cancel</button>
                      <button
                        className="btn-success"
                        onClick={handleSave}
                        disabled={!prospectName.trim()}
                      >
                        {estimateId ? 'Update →' : 'Save & Download PDF →'}
                      </button>
                    </div>
                  </div>
                )}

                {saveState === 'saving' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="spinner" style={{ width: '20px', height: '20px', margin: 0 }} />
                    <span style={{ color: 'var(--color-text-secondary)' }}>
                      {estimateId ? 'Updating estimate…' : 'Saving estimate…'}
                    </span>
                  </div>
                )}

                {saveState === 'saved' && (
                  <div>
                    <div className="alert success" style={{ marginBottom: '0.75rem' }}>
                      {estimateId
                        ? <>Estimate updated for <strong>{prospectName}</strong>.</>
                        : <>Estimate saved for <strong>{prospectName}</strong>.</>}
                    </div>
                    <Suspense fallback={<button className="btn-success" disabled>Preparing PDF…</button>}>
                      <EstimateDownloadButton
                        estimate={{
                          prospectName: prospectName.trim(),
                          region,
                          os: osDerived,
                          generatedAt: savedAt,
                          groupResults: groupResults.map(g => ({
                            label:        g.label,
                            bundleKey:    g.bundleKey,
                            bundleName:   BUNDLE_INFO[g.bundleKey]?.name || g.bundleKey,
                            storageKey:   g.resolvedStorageKey,
                            cheaperMode:  g.cheaperMode,
                            breakEven:    g.breakEven,
                            count:        g.count,
                            hours:        g.hours,
                            monthlyTotal: g.monthlyTotal,
                            perUserMo:    g.monthlyTotal != null ? g.monthlyTotal / g.count : null,
                            rates:        g.rates,
                            os:           g.os,
                          })),
                          totalMonthly,
                          totalWsCount,
                          wsSavings,
                          itSavings,
                          clickMonthly,
                          roi,
                        }}
                        fileName={`WorkSpaces-Estimate-${prospectName.trim().replace(/\s+/g, '-')}.pdf`}
                      />
                    </Suspense>
                  </div>
                )}

                {saveState === 'error' && (
                  <div>
                    <div className="alert error" style={{ marginBottom: '0.75rem' }}>
                      Something went wrong saving the estimate. Please try again.
                    </div>
                    <button onClick={() => setSaveState('form')}>Retry</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
